import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { forkJoin } from 'rxjs';
import { IPatientSpaceProfile } from '../../_interfaces/IPatientSpace';
import { AuthenticationService } from '../../_services/authentication/authentication.service';
import { SessionService } from '../../_services/session/session.service';
import { PatientSpaceService } from '../../_services/patient-space/patient-space.service';
import { FormUpdatePasswordComponent } from '../../components/forms/form-update-password/form-update-password.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-patient-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    FileUploadModule,
    FormUpdatePasswordComponent,
  ],
  templateUrl: './patient-profile.component.html',
  styleUrl: './patient-profile.component.scss',
})
export class PatientProfileComponent implements OnInit {
  loading = true;
  saving = false;
  activeTab: 'personal' | 'account' | 'security' | 'privacy' = 'personal';
  userId = 0;
  email = '';
  imageError = false;
  uploadURL = '';
  isPatientMode = false;
  hasLocalPassword = true;
  form = {
    name: '',
    surname: '',
    phone: '',
    birthday: '',
    gender: '',
    address: '',
    postcode: '',
    picture: '',
  };
  privacy = {
    share_name: false,
    share_surname: false,
    share_email: false,
    share_phone: false,
    share_birthday: false,
    share_gender: false,
    share_address: false,
    share_postcode: false,
  };

  constructor(
    private readonly patientSpaceService: PatientSpaceService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly session: SessionService,
    private readonly authService: AuthenticationService,
  ) {}

  private getSessionPicture(): string {
    return `${this.session.getUser()?.picture || ''}`.trim();
  }

  private resolveContextCapabilities(): void {
    const context = this.session.getContext();
    this.isPatientMode = context?.session?.active_mode === 'patient';
    const providers = Array.isArray(context?.authProviders)
      ? context.authProviders
      : [];
    this.hasLocalPassword = providers.length
      ? providers.includes('LOCAL')
      : true;
    if (!this.isPatientMode && this.activeTab === 'privacy') {
      this.activeTab = 'personal';
    }
  }

  ngOnInit(): void {
    this.resolveContextCapabilities();
    forkJoin({
      profile: this.patientSpaceService.profile(),
      me: this.authService.getMe(),
    }).subscribe({
      next: ({ profile, me }: { profile: IPatientSpaceProfile; me: any }) => {
        const user = profile?.user || ({} as any);
        const patient = profile?.patient || ({} as any);
        const globalProfile = profile?.globalPatientProfile || ({} as any);
        const meUser = me || ({} as any);
        const resolvedPicture = `${globalProfile?.picture || user?.picture || meUser?.picture || this.getSessionPicture() || ''}`.trim();
        this.form = {
          name: `${globalProfile?.name || patient?.name || user?.name || ''}`.trim(),
          surname: `${globalProfile?.surname || patient?.surname || user?.surname || ''}`.trim(),
          phone: `${globalProfile?.phone || patient?.phone || ''}`.trim(),
          birthday: this.normalizeDateInput(
            globalProfile?.birthday || patient?.birthday || user?.birthday,
          ),
          gender: this.normalizeGenderInput(
            globalProfile?.gender || patient?.gender || '',
          ),
          address: `${globalProfile?.address || patient?.address || ''}`.trim(),
          postcode: `${globalProfile?.postcode || patient?.postcode || ''}`.trim(),
          picture: resolvedPicture,
        };
        this.imageError = false;
        this.userId = Number(user?.id || meUser?.id || 0) || 0;
        this.uploadURL = this.userId
          ? `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/users/${this.userId}/settings/picture`
          : '';
        this.email = `${user?.email || meUser?.email || patient?.email || ''}`.trim();
        const privacy = (profile?.privacy || {}) as any;
        this.privacy = {
          share_name: !!privacy.share_name,
          share_surname: !!privacy.share_surname,
          share_email: !!privacy.share_email,
          share_phone: !!privacy.share_phone,
          share_birthday: !!privacy.share_birthday,
          share_gender: !!privacy.share_gender,
          share_address: !!privacy.share_address,
          share_postcode: !!privacy.share_postcode,
        };
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private normalizeDateInput(value: string | Date | null | undefined): string {
    if (!value) return '';
    const raw = typeof value === 'string' ? value : new Date(value).toISOString();
    return raw.slice(0, 10);
  }

  private normalizeGenderInput(value: string | null | undefined): string {
    const raw = `${value || ''}`.trim().toUpperCase();
    if (raw === 'M') return 'MALE';
    if (raw === 'F') return 'FEMALE';
    if (raw === 'O') return 'OTHER';
    if (raw === 'MALE' || raw === 'FEMALE' || raw === 'OTHER') return raw;
    return '';
  }

  get avatarUrl(): string {
    return `${this.form.picture || this.getSessionPicture() || ''}`.trim();
  }

  get initials(): string {
    const name = `${this.form.name || ''}`.trim();
    const surname = `${this.form.surname || ''}`.trim();
    const value = `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();
    return value || 'U';
  }

  get requiresMinimumProfile(): boolean {
    return !`${this.form.name || ''}`.trim() ||
      !`${this.form.surname || ''}`.trim() ||
      !`${this.form.phone || ''}`.trim();
  }

  onAvatarError() {
    this.imageError = true;
  }

  onPictureUploaded(event: any): void {
    const response = event?.originalEvent?.body || {};
    const picture = `${response?.picture || response?.filename || ''}`.trim();
    if (!picture) return;
    this.form.picture = picture;
    this.imageError = false;
    this.authService.setUserPicture(picture);
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('COMMON.SUCCESS'),
      detail: this.translate.instant('PATIENT_SPACE.PROFILE.PICTURE_UPDATED'),
    });
  }

  onPictureUploadError(event: any): void {
    const detail =
      event?.error?.error?.message ||
      this.translate.instant('PATIENT_SPACE.PROFILE.PICTURE_UPDATE_ERROR');
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('COMMON.ERROR'),
      detail,
    });
  }

  save() {
    if (this.saving) return;
    this.saving = true;
    const profilePayload: any = {
      name: this.form.name,
      surname: this.form.surname,
      phone: this.form.phone,
      birthday: this.form.birthday || null,
      gender: this.form.gender,
      address: this.form.address,
      postcode: this.form.postcode,
      picture: this.form.picture,
    };

    if (this.isPatientMode) {
      profilePayload.privacy = this.privacy;
    }

    this.patientSpaceService
      .updateProfile(profilePayload)
      .subscribe({
        next: () => {
          this.authService.setUserPicture(this.form.picture);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SUCCESS'),
            detail: this.translate.instant('PATIENT_SPACE.PROFILE.SAVED'),
          });
          this.saving = false;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.translate.instant('PATIENT_SPACE.PROFILE.SAVE_ERROR'),
          });
          this.saving = false;
        },
      });
  }
}
